import { Panel, PanelState } from "../panel/Panel";
import * as PIXI from "pixi.js";
import { observer } from "src/shared/util/Observer";
import { SpinResponse } from "src/shared/util/Types";
import { GameState, GlobalState } from "src/shared/util/GlobalState";
import { UI, UIEvent } from "src/shared/UI/UI";
import { GameEngine } from "../server/GameEngine";
import { GameAssets } from "src/shared/util/GameAssets";
import { delay } from "src/shared/util/UtilFunctions";
import gsap from "gsap";
import AppSingleton from "src/shared/util/AppSingleton";

export class GameView extends PIXI.Container {
  private app: PIXI.Application<PIXI.Renderer>;

  private content: PIXI.Container;
  private background: PIXI.Sprite;
  private fsBackground: PIXI.Sprite;
  private panel: Panel;
  private UI!: UI;
  private response!: SpinResponse;
  private gameState: GameState = GameState.MAIN;
  private timeline!: gsap.core.Timeline | null;

  constructor() {
    super();

    observer.on('gameActionResponse', this.onSpinResponse.bind(this));

    this.app = AppSingleton.getInstance().app;
    this.app.stage.on('resize', this.resize, this);

    window.addEventListener('keydown', this.handleKeyDown.bind(this));

    this.content = new PIXI.Container();
    this.addChild(this.content);

    this.background = new PIXI.Sprite(GameAssets.textures.mainBg);
    this.background.anchor.set(0.5);

    this.fsBackground = new PIXI.Sprite(GameAssets.textures.fsBg);
    this.fsBackground.anchor.set(0.5);
    this.fsBackground.visible = false;

    this.panel = new Panel();
    this.panel.zIndex = 3;
    this.panel.on(PanelState.IDLE, this.onPanelIdle, this);
    this.panel.on(PanelState.CASCADE, this.onCascade, this);
    this.panel.on('resetQuickStop', this.onResetQuickStop, this);
    this.panel.on('showRandomWilds', this.zoomIn, this);

    this.UI = new UI();
    this.UI.zIndex = 4;
    this.UI.on('buy', this.onBuy, this);
    this.UI.on('spin', this.onSpin, this);
    this.UI.on('stop', this.onQuickStop, this);
    this.UI.updateVisualBalance();
    this.UI.setWin(0);

    this.addComponentsOnStage();

    // this.UI.lock();
    // this.UI.hideBar();
    // this.UI.once(UIEvent.SCREEN_CLICK, this.onIntroClick.bind(this));

    this.content.visible = true;
    this.UI.resetSpinButton();
    this.UI.showBar();
    this.panel.init(GameEngine.getInstance().getLoadResponse());
    this.UI.updateVisualBalance();
  }

  onPortrait(data?: any) {
    this.background.scale.set(2.7);
    this.fsBackground.scale.set(this.background.scale.x);
    this.background.y = -800;
    this.fsBackground.y = this.background.y;

    this.panel.onPortrait(data);
  }

  onLandscape(data?: any) {
    this.background.scale.set(1.1);
    this.background.y = -80;
    this.fsBackground.scale.set(this.background.scale.x);
    this.fsBackground.y = this.background.y;

    this.panel.onLandscape(data);
  }

  addComponentsOnStage() {
    this.content.addChild(this.background);
    this.content.addChild(this.fsBackground);
    this.content.addChild(this.panel);
    this.addChild(this.UI);
  }

  handleKeyDown(event: any) {
    switch (event.code) {
    }
  }

  async onPanelIdle() {
    switch (GlobalState.getInstance().state) {
      case GameState.MAIN:
        this.UI.resetSpinButton();
        this.UI.updateVisualBalance();
        this.UI.setWin(this.response.roundWin);
        break;
      case GameState.BONUS:
        delay(200);
        this.onSpin();
        this.UI.setWin(this.response.totalWin || 0);
        break;
      case GameState.MAIN_TO_BONUS:
        this.fsBackground.visible = true;
        this.fsBackground.alpha = 0;

        this.background.zIndex = 0;
        this.fsBackground.zIndex = 1;
        gsap.to(this.fsBackground, {
          alpha: 1,
          duration: 0.5,
          onComplete: () => {
            this.background.visible = false;
          },
        });
        this.UI.lock();
        await this.panel.showBonusIntro();
        this.UI.once(UIEvent.SCREEN_CLICK, this.transitionToBonus.bind(this));
        this.UI.setWin(this.response.roundWin);
        break;
      case GameState.BONUS_TO_MAIN:
        this.UI.lock();
        await this.panel.showBonusWin();
        this.UI.once(UIEvent.SCREEN_CLICK, this.transitionToMain.bind(this));
        this.UI.setWin(this.response.totalWin || 0);
        this.UI.updateVisualBalance();
        break;
    }
  }

  onResetQuickStop() {
    this.UI.setStopButton();
  }

  onCascade() {
    if (GlobalState.getInstance().state === GameState.BONUS && this.response.totalWin) {
      this.UI.setWin(this.response.totalWin);
    } else {
      this.UI.setWin(this.response.roundWin);
    }
    GameEngine.getInstance().gameActionRequest();
  }

  onBuy() {
    this.startSpin(true);
    this.UI.setWin(0);
    this.UI.setStopButton();
  }

  async onSpin() {
    switch (GlobalState.getInstance().state) {
      case GameState.MAIN:
      case GameState.MAIN_TO_BONUS:
      case GameState.BONUS_TO_MAIN:
      case GameState.BONUS:
        GameEngine.getInstance().gameActionRequest();
        this.startSpin();
        this.UI.setWin(0);
        break;
    }
  }

  async onSpinResponse(response: SpinResponse) {
    this.response = response;
    this.panel.setData(this.response);
  }

  private zoomIn() {
    this.timeline = gsap.timeline()
      .to(this.content.scale, {
        x: 1.1,
        y: 1.1,
        duration: 2,
        ease: "power2.out",
      }).to(this.content.scale, {
        x: 1,
        y: 1,
        duration: 1,
        ease: "power2.out",
      });
  }

  private transitionToBonus() {
    this.panel.hideBonusIntro().then(() => {
      GlobalState.getInstance().updateState({ state: GameState.BONUS })
      this.onSpin();
    });
  }

  private transitionToMain() {
    this.panel.hideBonusWin().then(() => {
      this.background.visible = true;
      this.background.alpha = 0;

      this.background.zIndex = 1;
      this.fsBackground.zIndex = 0;
      gsap.to(this.background, {
        alpha: 1,
        duration: 0.5,
        onComplete: () => {
          this.fsBackground.visible = false;
        },
      });
      GlobalState.getInstance().updateState({ state: GameState.MAIN })
      this.UI.resetSpinButton();
    });
  }

  private onQuickStop() {
    switch (GlobalState.getInstance().state) {
      case GameState.MAIN_TO_BONUS:
      case GameState.BONUS_TO_MAIN:
      case GameState.MAIN:
      case GameState.BONUS:
        // Stop and kill the existing timeline if it exists
        if (this.timeline) {
          this.timeline.kill();
          this.content.scale.set(1, 1);
          this.timeline = null; // Clear reference
        }
        this.panel.quickStop();
        break;
    }
  }

  private resize(data: any) {
    if (data.ratio < 1.4) {
      this.onPortrait(data);
    } else {
      this.onLandscape(data);
    }
  }

  private startSpin(isBuy: boolean = false) {
    if (this.panel.getState() === PanelState.IDLE) {
      // Don't decrease the balance on Freespins start
      if (GlobalState.getInstance().state === GameState.BONUS_TO_MAIN || GlobalState.getInstance().state === GameState.MAIN) {
        GlobalState.getInstance().updateState({ balance: GlobalState.getInstance().balance - GlobalState.getInstance().bet });
        this.UI.updateVisualBalance();
      } else {
        this.UI.updateVisualBalance();
      }
      this.panel.spin();
      if (GlobalState.getInstance().fastOn) {
        this.panel.quickStop();
      }
    }
  }
}
